import { Component, Element, Event, EventEmitter, h, Method, Prop, State, Watch } from '@stencil/core';
import Bind from 'bind-decorator';
import { StyleCollection } from '../../../types';
import { capitalize } from '../../../utils/capitalize';
import { generateCssClasses } from '../../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../../utils/generate-unique-uuid';
import { hasSlotted } from '../../../utils/slotted';
import { handleBodyAnimationsClosed, handleBodyAnimationsOpen } from '../animations-util';
import { NavigationAnimateEventDetail, NavigationEventDetail } from '../zds-navigation';

type NavigationBodyState = 'open' | 'opened' | 'close' | 'closed';

@Component({
	shadow: true,
	styleUrl: 'zds-navigation-body.scss',
	tag: 'zds-navigation-body',
})
export class ZdsNavigationBody {
	bodyElement: HTMLDivElement;
	keyboardScrollInterval?: NodeJS.Timeout;
	timeout: ReturnType<typeof setTimeout>;

	@Element() hostElement: HTMLElement;

	@Event({ bubbles: true }) zdsNavigationBodyClose: EventEmitter<NavigationAnimateEventDetail>;
	@Event({ bubbles: true }) zdsNavigationBodyClosed: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationBodyOpen: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationBodyOpened: EventEmitter<NavigationEventDetail>;

	@Prop() identifier: string = generateUniqueUuid('zds-navigation-body');

	@State() bodyHeight: number = null;
	@State() shouldAnimate: boolean = null;
	@State() state: NavigationBodyState = 'closed';

	@Watch('state')
	onStateChange(newValue: string) {
		// The 'close' state has additional details
		if (newValue === 'close') {
			this.zdsNavigationBodyClose.emit({
				identifier: this.identifier,
				shouldAnimate: this.shouldAnimate,
			});
			return;
		}

		// Handle any other state
		this[`zdsNavigationBody${capitalize(newValue)}`].emit({ identifier: this.identifier });
	}

	@Method()
	async toggle(
		toggle: boolean = this.state === 'close' || this.state === 'closed',
		shouldAnimate: boolean = true,
	): Promise<void> {
		clearTimeout(this.timeout);

		this.shouldAnimate = shouldAnimate;
		const timeoutDuration = shouldAnimate ? 400 : 0;

		this.state = toggle ? 'open' : 'close';
		this.timeout = setTimeout(this.finishStateChange, timeoutDuration, this.state);

		// Make sure the element is visible, otherwise animations will not be executed
		this.bodyElement.style.display = toggle ? 'block' : null;

		if (toggle && shouldAnimate) {
			handleBodyAnimationsOpen(this.hostElement);
		}

		// Allow caller to wait for completion
		if (shouldAnimate) {
			return new Promise((resolve) => setTimeout(resolve, 400));
		}

		this.shouldAnimate = null;
	}

	@Method()
	async setBodyHeight(newHeight: number) {
		this.bodyHeight = newHeight;
	}

	componentDidLoad() {
		handleBodyAnimationsClosed(this.hostElement);
	}

	disconnectedCallback() {
		this.disableKeyboardScrollHandler();
	}

	disableKeyboardScrollHandler() {
		if (!this.keyboardScrollInterval) return;
		clearInterval(this.keyboardScrollInterval);
	}

	enableKeyboardScrollHandler() {
		this.keyboardScrollInterval = setInterval(this.handleKeyboardScroll, 500);
	}

	@Bind
	finishStateChange(state: NavigationBodyState) {
		if (state === 'open') {
			this.state = 'opened';
			this.enableKeyboardScrollHandler();
		} else if (state === 'close') {
			this.state = 'closed';
			this.disableKeyboardScrollHandler();
			handleBodyAnimationsClosed(this.hostElement);
		}
	}

	@Bind
	handleKeyboardScroll() {
		if (document.documentElement.scrollTop === 0) return;

		this.bodyElement.scrollTop += document.documentElement.scrollTop;
		document.documentElement.scrollTop = 0;
	}

	render() {
		const bodyClasses = generateCssClasses('zds-navigation-body__body', {
			[this.state]: true,
		});

		const bodyStyle: StyleCollection = {
			height: this.bodyHeight + 'px',
		};

		return (
			<div class="zds-navigation-body">
				<slot name="navigation-link" />
				<div class={bodyClasses} style={bodyStyle} ref={(element) => (this.bodyElement = element)}>
					<div class="zds-navigation-body__wrapper">
						{this.renderTitle()}
						{this.renderSubText()}
						<slot />
					</div>
				</div>
			</div>
		);
	}

	renderSubText() {
		if (!hasSlotted(this.hostElement, 'subtext')) {
			return null;
		}

		return (
			<div class="zds-navigation-body__subtext">
				<slot name="subtext"></slot>
			</div>
		);
	}

	renderTitle() {
		if (!hasSlotted(this.hostElement, 'title')) {
			return null;
		}

		return (
			<div class="zds-navigation-body__title">
				<slot name="title"></slot>
			</div>
		);
	}
}
